import {
  BOOKING_PAYMENT_STATUSES,
  BOOKING_STATUSES,
  getSessionDisplayLabel,
  STATUS_SESSION_CANCELLED,
  SUBTYPE_POST_CONSULTATION,
  SUBTYPE_PRE_CONSULTATION,
  VISIT_EVENT_TYPES,
} from 'src/consumer/constants'
import StepsCheckUI from 'src/clinic/components/ClinicManageBookingsPage/screening/StepsCheckUI'

function StatusBadge({ color, background, children }) {
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        color,
        backgroundColor: background,
        padding: '2px 6px',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}
    >
      {children}
    </span>
  )
}

/**
 * Surfaces the same event-type / screening / status details shown on the calendar
 * strip, for an Appointments row: the session label, screening + step statuses
 * (reused from StepsCheckUI) and the strip's status badges.
 */
export default function ConsultsDetailsCell({ bookedSlot }) {
  const label = getSessionDisplayLabel(bookedSlot)
  const title = bookedSlot?.title

  const isEmergency = bookedSlot?.visitEventType === VISIT_EVENT_TYPES.emergency
  const isCancelled = bookedSlot?.status === STATUS_SESSION_CANCELLED
  const isPaymentPending =
    bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
  const isPaidOnAumhum = bookedSlot?.feePaymentMode === 'aumhum'
  const isPreRx = bookedSlot?.eventSubType === SUBTYPE_PRE_CONSULTATION
  const isPostRx = bookedSlot?.eventSubType === SUBTYPE_POST_CONSULTATION
  const isRecording = Boolean(bookedSlot?.recordingEnabled)
  const wasDelayed = Boolean(bookedSlot?.wasDelayedBecauseOfOverlaps)

  const hasAnyBadge =
    isEmergency ||
    isCancelled ||
    isPaymentPending ||
    isPaidOnAumhum ||
    isPreRx ||
    isPostRx ||
    isRecording ||
    wasDelayed

  return (
    <td>
      <div className="d-flex flex-column gap-1" style={{ minWidth: '180px' }}>
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <span style={{ fontWeight: 600 }}>{label}</span>
          {title && title !== label && (
            <span style={{ color: '#6b7280', fontSize: '12px' }}>· {title}</span>
          )}
        </div>

        {hasAnyBadge && (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            {isEmergency && (
              <StatusBadge color="#ffffff" background="#dc2626">
                🚨 Emergency
              </StatusBadge>
            )}
            {isPaymentPending && (
              <StatusBadge color="#7c2d12" background="rgba(251, 146, 60, 0.2)">
                Payment pending
              </StatusBadge>
            )}
            {isCancelled && (
              <StatusBadge color="#dc2626" background="rgba(220, 38, 38, 0.15)">
                Cancelled
              </StatusBadge>
            )}
            {isPreRx && (
              <StatusBadge color="#6366f1" background="rgba(99, 102, 241, 0.15)">
                Pre-Rx
              </StatusBadge>
            )}
            {isPostRx && (
              <StatusBadge color="#8b5cf6" background="rgba(139, 92, 246, 0.15)">
                Post-Rx
              </StatusBadge>
            )}
            {isPaidOnAumhum && (
              <StatusBadge color="#f59e0b" background="rgba(245, 158, 11, 0.15)">
                Paid on AUMHUM
              </StatusBadge>
            )}
            {isRecording && (
              <StatusBadge color="#dc2626" background="rgba(220, 38, 38, 0.12)">
                ● Rec
              </StatusBadge>
            )}
            {wasDelayed && (
              <StatusBadge color="#92400e" background="rgba(245, 158, 11, 0.15)">
                ⏰ Shifted
              </StatusBadge>
            )}
          </div>
        )}

        {/* Screening / therapy step statuses (vitals shown in their own column) */}
        <StepsCheckUI bookedSlot={bookedSlot} hideVitals />
      </div>
    </td>
  )
}
